/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef, useState} from 'react'
import {FormattedMessage} from 'react-intl'
import {Box, Stack, Grid, Skeleton, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import NoOrdersAvailable from './no-orders-available'
import OrderListCard from './order-history-list-card'
import {MRT_API_BASEPATH} from '../../../ssr/constants'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {BRAND_SHORT_LABEL} from '../../../brand'
import ItemList from '../../../components/item-list'
import {ORDERS_HISTORY_SEARCH_PARAMS} from '../constant'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {useTokenHelper} from '../../../utils/token-helper'
// import {useLocation} from 'react-router'
// import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
// import {Helmet} from 'react-helmet'

const OrderHistory = () => {
    const {data: customer} = useCurrentCustomer()
    const searchParams = useSearchParams(ORDERS_HISTORY_SEARCH_PARAMS)
    const {limit, offset} = searchParams[0]
    const appOrigin = useOrigin(false)
    const [orders, setOrders] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState(null)
    const headingRef = useRef()
    const {getTokenWhenReady} = useAccessToken()
    const {fetchOMSValidToken} = useTokenHelper()
    const styles = useMultiStyleConfig('OrderHistory')
    // const location = useLocation()
    // const {customParams} = getConfig()
    // const currentPath = 'https://' + customParams.currentHost + location.pathname
    // const webPageSchemaMarkup = {
    //     '@context': 'https://schema.org/',
    //     '@type': 'WebPage',
    //     name: 'Order History',
    //     description: 'View and manage your orders',
    //     url: currentPath,
    // }

    useEffect(() => {
        // Focus the 'Order History' header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [customer, searchParams.offset])

    useEffect(() => {
        const fetchOrders = async () => {
            setIsLoading(true)
            try {
                const accessToken = await fetchOMSValidToken()

                const queryParams = new URLSearchParams({
                    gigyaId: customer.c_gigyaId,
                    sfAccountEmail: customer.email,
                    brand: BRAND_SHORT_LABEL,
                    limit,
                    offset,
                })
                const token = await getTokenWhenReady()
                // Use the token from getTokenWhenReady for Commerce Cloud APIs
                // and accessToken for OMS APIs

                const response = await fetch(
                    `${appOrigin}${MRT_API_BASEPATH}/get-order-list-oms?${queryParams.toString()}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${accessToken}`, // OMS token
                            customerId: customer?.customerId,
                            'x-commercecloud-token': `Bearer ${token}`, // Send Commerce Cloud token as a custom header
                        },
                    },
                )

                const result = await response.json()
                if (result.success) {
                    setOrders(result.orders)
                } else {
                    setError('Failed to fetch order list: ' + result.error)
                }
            } catch (error) {
                console.error('Error fetching order list:', error)
                setError('Error fetching order list: ' + error.message)
            } finally {
                setIsLoading(false)
            }
        }

        if (customer?.email) {
            fetchOrders()
        }
    }, [customer?.email])

    const hasOrders = orders.length > 0

    return (
        <Stack {...styles?.outerStack} data-testid="account-order-history-page">
            {/* <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet> */}
            <Text as="h1" ref={headingRef} {...styles?.title}>
                <FormattedMessage
                    defaultMessage="Order History"
                    id="account_order_history.title.order_history"
                />
            </Text>

            {error && (
                <Box {...styles?.errorBox}>
                    <Text {...styles?.errorText}>{error}</Text>
                </Box>
            )}

            {isLoading ? (
                [1, 2, 3].map((i) => (
                    <Stack key={i} spacing={4} layerStyle="cardBordered" data-testid="skeletons">
                        <Stack spacing={2}>
                            <Skeleton h="20px" w="112px" />
                            <Skeleton h="20px" w="200px" />
                        </Stack>
                        <Grid templateColumns={{base: 'repeat(auto-fit, 88px)'}} gap={4}>
                            {Array.from(Array(4).keys()).map((i) => (
                                <Skeleton key={i} w="88px" h="88px" />
                            ))}
                        </Grid>
                        <Skeleton h="20px" w="200px" />
                    </Stack>
                ))
            ) : (
                <>
                    {hasOrders && (
                        <Stack spacing={0}>
                            <ItemList
                                items={orders}
                                renderItem={(order, index) => (
                                    <OrderListCard key={order?.Id || index} order={order} />
                                )}
                            />
                        </Stack>
                    )}
                </>
            )}

            {!hasOrders && !isLoading && <NoOrdersAvailable />}
        </Stack>
    )
}

OrderHistory.getTemplateName = () => 'account-order-history'

export default OrderHistory
